import { Entity, DetailEvent, registerShader, registerSystem, System } from "aframe";
import { AnimationDefinition, AvatarActionInstance, AvatarComponent, AvatarEntity, SetAnimationEventDetail } from "../avatar";
import { InputActionDetail } from "../input/common";
import { ClipSystem } from "./clip";


const getClipForHeight = (clipSystem : ClipSystem, clip : string, height : number) =>
{
	if(height > 1.8)
	{
		const clipHigh = clip + "High";
		return (clipHigh in clipSystem.clips) ? clipHigh : clip;
	}
	if(height < 0.6)
	{
		const clipGround = clip + "Ground";
		if(clipGround in clipSystem.clips) return clipGround;
	}
	if(height < 1.2)
	{
		const clipLow = clip + "Low";
		if(clipLow in clipSystem.clips) return clipLow;
	}
	return clip;
}


interface AnimationActionSystem extends System
{
	createInstance(avatar : AvatarComponent, input : InputActionDetail, clip : string | string[], target? : Entity, orientationTarget? : Entity) : AvatarActionInstance;
}
registerSystem("animation-action", {
	createInstance(this : AnimationActionSystem, avatar : AvatarComponent, input : InputActionDetail, clip : string | string[], target? : Entity, orientationTarget? : Entity)
	{
		return new AnimationActionInstance(avatar, input, clip, target, orientationTarget);
	}
})

export class AnimationActionInstance implements AvatarActionInstance
{
	playing : boolean = true;
	completed? : boolean; //When finishsed (!playing), indicates whether the animation reached its end or was interrupted.
	finished : Promise<boolean>;
	orientationTarget? : Entity;

	constructor(avatar : AvatarComponent, input : InputActionDetail, clip : string | string[], target? : Entity, orientationTarget? : Entity)
	{
		this.orientationTarget = orientationTarget;

		const clips : string[] = Array.isArray(clip) ? clip : [ clip, "" ];
		if(orientationTarget)
		{
			orientationTarget.object3D.updateWorldMatrix(true, false);
			avatar.el.object3D.updateWorldMatrix(true, false);
			
			const deltaY = orientationTarget.object3D.matrixWorld.elements[13] - avatar.el.object3D.matrixWorld.elements[13];
			
			const clipSystem = avatar.el.sceneEl!.systems.clip as ClipSystem;
			for(let ix = clips.length; --ix >= 0; )
				clips[ix] = getClipForHeight(clipSystem, clips[ix], deltaY);
		}

		this.finished = new Promise<boolean>( (done, _ ) =>
		{
			const eventTarget = target || (input.input || avatar).el;
			const eventDetail : SetAnimationEventDetail = {
				level : 10,
				animations : {
					"^(Idle)$" : { filter : "", clips },
					".*" : { filter : "Shoulder(\/|$)", clips }
				}
			};

			const setAnimationListener = (animEvent : DetailEvent<SetAnimationEventDetail>) =>
			{
				if(animEvent.detail === eventDetail) return; //Just in case.
				if(animEvent.detail.level == 10) //Another animation started
				{
					this.completed = false;
					this.playing = false;
					avatar.el.removeEventListener("clip-ended", endListener as any);
					avatar.el.removeEventListener("set-animation", setAnimationListener as any, { capture : true });
					return done(false);
				}
			};

			const endListener = (clipEndedEvent : DetailEvent<{filter:string, clip:string}>) => 
			{
				for(let key in eventDetail.animations)
				{
					const anim = eventDetail.animations[key] as AnimationDefinition;
					if( clipEndedEvent.detail.filter != anim.filter ) continue;
					if( clipEndedEvent.detail.clip != anim.clips[0] ) continue;

					this.completed = true;
					this.playing = false;
					avatar.el.removeEventListener("clip-ended", endListener as any);
					avatar.el.removeEventListener("set-animation", setAnimationListener as any, { capture : true });
					eventTarget.emit("set-animation", { level : 10, animations : {} } );
					return done(true);
				}
			};

			avatar.el.addEventListener("clip-ended", endListener as any);
			eventTarget.emit("set-animation", eventDetail);
			//Listen for set-animation before avatar handles it
			avatar.el.addEventListener("set-animation", setAnimationListener as any, { capture : true });
		});
	}
	tick(avatar : AvatarComponent, t: number, dt: number): boolean 
	{ 
		if(!this.playing) return false;

		if(this.orientationTarget)
		{
			//Never target something in the avatar's tree
			for(let p = this.orientationTarget; p; p = p.parentEl!)
				if(p === avatar.el)
					return true;

			this.orientationTarget.object3D.updateWorldMatrix(true, false);
			avatar.el.object3D.updateWorldMatrix(true, false);
			const tMat = this.orientationTarget.object3D.matrixWorld.elements;
			const aMat = avatar.el.object3D.matrixWorld.elements;
			avatar.orientation = Math.atan2( tMat[12] - aMat[12], tMat[14] - aMat[14] );
		}

		return true; 
	}
	destroy(avatar : AvatarComponent): void {} 
}

